"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import SectionHeader from "../sectionHeader";
import ArcadePrizeMachine from "../arcade/ArcadePrizeMachine";

interface Props {
  data?: any;
}

export default function TextSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
  });
  const hasArcade = Boolean(data?.arcade);
  const hasVisual = Boolean(data?.image || hasArcade);
  const smallImage = !hasArcade && data?.imageSize === "small";

  return (
    <m.section
      className={`container mx-auto grid ${data?.compact ? "gap-4 py-4" : "gap-16 section"} ${hasVisual ? "md:grid-cols-2 gap-4 " : ""} ${data?.darkerBackground ? "bg-neutral-200/50" : ""}`}
      ref={ref}
    >
      {hasVisual && (
        <div
          className={`${hasArcade ? "order-2 md:order-1 overflow-hidden relative min-h-[660px] md:min-h-[680px]" : "overflow-hidden relative aspect-square"} ${smallImage ? "w-full max-w-[18rem] justify-self-center md:self-center" : ""} flex items-center justify-center bg-neutral-100`}
          style={data?.image?.bgColor ? { backgroundColor: data.image.bgColor } : undefined}
        >
          {hasArcade ? (
            <ArcadePrizeMachine variant={data.arcade.variant} locale={data.arcade.locale} />
          ) : (
            <m.div
              className="flex justify-center static w-full h-full"
              initial={{ height: 0 }}
              animate={{
                height: isInView ? "100%" : 0,
              }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Image
                src={data.image.asset.url}
                alt={data.image.alt ?? data.headline ?? "TMRW marketing visual"}
                width={1000}
                height={1000}
                sizes="(min-width: 768px) 50vw, 100vw"
                className={`${data.image.bgColor ? "object-contain" : "object-cover"} h-full w-full`}
                style={
                  data?.imageScale
                    ? {
                        transform: `scale(${data.imageScale})`,
                        transformOrigin: data?.imagePosition ?? "center",
                      }
                    : undefined
                }
              />
            </m.div>
          )}
        </div>
      )}
      <div
        className={`flex flex-col justify-center gap-8 ${hasArcade ? "order-1 md:order-2" : ""} ${data?.compact ? "items-start mx-auto max-w-3xl w-full" : "items-start"} ${hasVisual ? "frame px-4 py-8 lg:px-8" : ""}`}
      >
        <SectionHeader
          title={data?.title}
          headline={data?.headline}
          headerIcon={data?.headerIcon}
          headlineSize={data?.compact ? "h4" : hasVisual ? "h3" : "h2"}
          maxWidth={hasVisual ? "max-w-3xl" : "max-w-7xl"}
        />
        <div
          className={`text-pretty flex flex-col gap-8 ${data?.compact ? "text-base" : "text-lg"} ${hasVisual ? "text-left max-w-xl" : data?.fullWidth ? "max-w-full" : "max-w-2xl"}`}
        >
          <div className="flex flex-col gap-4">
            {data?.byline && (
              <p>
                {data.byline.name}
                {data.byline.role ? `, ${data.byline.role}` : ""}
              </p>
            )}
            {(data?.content as string[] | undefined)?.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
          {data?.byline?.link && (
            <a
              aria-label={data.byline.link.ariaLabel}
              className="mono group inline-flex w-fit items-center gap-2 rounded bg-neutral-900 px-3 py-2 text-white transition-colors hover:bg-primary hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
              href={data.byline.link.href}
              rel="noopener noreferrer"
              target="_blank"
            >
              {data.byline.link.label}
              <span
                aria-hidden="true"
                className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              >
                ↗
              </span>
            </a>
          )}
        </div>
      </div>
    </m.section>
  );
}
